<script setup lang="ts">
import FrameTitleDirectory from "@/components/frames/FrameTitleDirectory.vue";
import ProductCard from "@/components/cards/ProductCard.vue";
import ATitle from "@/components/io/atomic/atoms/fragments/content/ATitle.vue";
import AContent from "@/components/io/atomic/atoms/fragments/content/AContent.vue";
import ListboxDropdown from '@/components/ListboxDropdown.vue';
import Spinner from '@/components/Spinner.vue';
import SectionContactWithRows from "@/components/sections/SectionContactWithRows.vue";
import MPDImage from "@/components/MPDImage.vue";

useSeoMeta({
  title: 'Food & Beverage Hygiene Products | Altracare',
  description: 'Sanitising solutions and back-of-house cleaning chemistry protect food prep, bars and service areas with rapid support across Zanzibar.',
  ogTitle: 'Food & Beverage Hygiene Products | Altracare',
  ogDescription: 'Sanitising solutions and back-of-house cleaning chemistry protect food prep, bars and service areas with rapid support across Zanzibar.',
  ogType: 'website',
  twitterCard: 'summary_large_image'
})

const { scrollToId } = useScrollTo()

const { hero } = useHero()

const productsStore = useProductsStore()

// Use global products initialization
const { waitForInitialization } = useGlobalProductsInit()

onMounted(() => {
  hero({
    media: {
      type: 'image',
      media: {
        src: '/images/food-header.jpg',
        alt: 'Food and Beverages'
      }
    },
    content: {
      model: 'page',
      content: {
        hairline: '',
        title: 'Food and Beverages',
        description: 'Powerful, food-safe hygiene solutions for spotless, compliant food and beverages.'
      }
    },
    overlays: {
      transition: { mode: 'out-in' },
      overlays: [{
          type: 'gradient',
          styles: {
            kind: 'linear',
            direction: 180, // top -> bottom
            stops: [
              { color: 'rgba(0,0,0,0.00)', at: '45%' },
              { color: 'rgba(0,0,0,0.75)', at: '80%' },
              { color: 'rgba(0,0,0,1.00)',  at: '100%' }
            ],
            blendMode: 'normal'
          },
          breakpoints: [
            {
              from: 1024, until: -1,
              styles: {
                stops: [
                  { color: 'rgba(0,0,0,0.00)', at: '40%' },
                  { color: 'rgba(0,0,0,0.85)', at: '78%' },
                  { color: 'rgba(0,0,0,1.00)',  at: '100%' }
                ]
              }
            }
          ]
        },{
          type: 'gradient',
          styles: {
            kind: 'linear',
            direction: 'to right',
            stops: [
              { color: 'rgba(0,40,140,0.45)', at: '0%'  },
              { color: 'rgba(0,40,140,0.20)', at: '35%' },
              { color: 'rgba(0,40,140,0.00)', at: '70%' }
            ],
            blendMode: 'soft-light'
          },
          breakpoints: [
            {
              from: 1024, until: -1,
              styles: {
                direction: 'to bottom right',
                stops: [
                  { color: 'rgba(0,40,140,0.50)', at: '0%'  },
                  { color: 'rgba(0,40,140,0.00)', at: '60%' }
                ],
                blendMode: 'multiply'
              }
            }
          ]
        }]
    },
  })
})

// Hardcoded parent category slug for kitchen products
const PARENT_CATEGORY_SLUG = 'kitchen-products' // food-beverages

// State
const selectedCategory = ref('')
const isInitializing = ref(false)

// Helper function to decode HTML entities
const decodeHtmlEntities = (text: string): string => {
  if (!text) return text
  if (typeof document === 'undefined') {
    // Server-side fallback
    return text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&nbsp;/g, ' ')
  }
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

// Computed properties from store
const loading = computed(() => productsStore.loading)
const subcategories = computed(() => {
  const subs = productsStore.getCategorySubcategories(PARENT_CATEGORY_SLUG)
  return subs
})

// Get products filtered by category
const filteredProducts = computed(() => {
  let filtered = productsStore.productList
  
  if (selectedCategory.value) {
    // Filter by the selected subcategory
    filtered = filtered.filter(product => {
      // Check primary category
      if (product.category === selectedCategory.value) return true
      
      // Check categories array
      if (product.categories && product.categories.length > 0) {
        return product.categories.some(cat => cat.slug === selectedCategory.value)
      }
      
      return false
    })
  } else {
    // Show all kitchen products (parent and subcategories)
    const kitchenCategoryIds = [PARENT_CATEGORY_SLUG, ...subcategories.value.map(sc => sc.slug)]
    filtered = filtered.filter(product => {
      // Check primary category
      if (kitchenCategoryIds.includes(product.category)) return true
      
      // Check categories array
      if (product.categories && product.categories.length > 0) {
        return product.categories.some(cat => kitchenCategoryIds.includes(cat.slug))
      }
      
      return false
    })
  }
  
  // Transform for ProductCard component
  return filtered.map(product => ({
    id: product.id,
    slug: product.slug,
    titles: {
      card: product.title,
      admin: product.title,
      short: product.title,
      long: product.title,
      page: product.title,
      friendly: product.title,
      introHairline: ''
    },
    description: {
      short: product.shortDescription,
      full: product.shortDescription
    },
    category: product.category,
    categoryName: product.categoryName,
    categories: product.categories,
    image: product.image,
    images: product.image ? [product.image] : [],
    origin: product.origin,
    denaturation: product.denaturation
  }))
})

// Dropdown options
const dropdownOptions = computed(() => {
  const options = [
    { label: 'All Kitchen Products', value: '' }
  ]
  
  subcategories.value.forEach(cat => {
    options.push({
      label: decodeHtmlEntities(cat.name),
      value: cat.slug
    })
  })
  
  return options
})

const selectedCategoryLabel = computed(() => {
  if (!selectedCategory.value) return 'All Kitchen Products'
  const cat = subcategories.value.find(c => c.slug === selectedCategory.value)
  return cat?.name ? decodeHtmlEntities(cat.name) : 'All Kitchen Products'
})

// Initialize data on mount
onMounted(async () => {
  isInitializing.value = true
  
  // Wait for global initialization to complete
  const initialized = await waitForInitialization()
  
  if (initialized) {
    console.log('[Kitchen Products Page] Using globally initialized products')
    console.log(`[Kitchen Products Page] Total products available: ${productsStore.productList.length}`)
    console.log(`[Kitchen Products Page] Kitchen subcategories: ${subcategories.value.length}`)
  } else {
    console.error('[Kitchen Products Page] Failed to initialize products')
  }
  
  isInitializing.value = false
})
</script>

<template>
  <Frame class="page-kitchen-products gpt-2 ds:gpt-3">

    <Frame class="sec-intro" w="max-w-90% mx-auto grid grid-cols-[1fr] ds:grid-cols-[1fr_1fr_1fr] g-1 gpy-2">
      <Frame class="sec-content" w="grid g-1">
        <ATitle class="a-title sphere-content-title">
          <template #hairline>
            <div class="a-title-hairline glh-1! text-[var(--heading-hairline)] gpb-1! ds:gpb-0!">
              Quality, safety and efficiency—designed together.
            </div>
          </template>
          <template #primary>
            <div class="a-title-primary fs-4! ds:fs-5! glh-2!">
              Beyond Clean — <br/>Confidence in Every Kitchen
            </div>
          </template>
        </ATitle>
        <Frame w="mx-auto grid g-1 justify-start">
          <p>In hospitality, kitchens aren’t just workspaces — they’re the foundation of safety and guest trust.</p>
          <p>Our kitchen products go further than surface shine: they’re designed to cut through grease, prevent cross-contamination, and help your team maintain consistent standards under pressure.</p>

        </Frame>
      </Frame>
      <Frame class="sec-intro-content" w="grid g-1">
        <Frame class="sec-image-grid" w="grid grid-cols-[1fr_1fr] g-1 t:max-w-80% mx-auto gp-1 ds:gp-0">
          <MPDImage class="grid-image" image="/images/grids/food-grid-1.jpg" alt="" />
          <MPDImage class="grid-image" image="/images/grids/food-grid-2.jpg" alt="" />
          <MPDImage class="grid-image" image="/images/grids/food-grid-3.jpg" alt="" />
          <MPDImage class="grid-image" image="/images/grids/food-grid-4.jpg" alt="" />
        </Frame>
      </Frame>
      
      <Frame class="sec-content" w="grid g-1">
       <div class="sec-features grid g-1">
        <div class="sec-feature flex items-start g-0.5 ">
          <div class="sec-feature-icon">
            <i class="i-mdi:check w-24px h-24px text-green-500 flex gmt-0.25" />
          </div>
          <div class="sec-feature-content">
            <strong class="text-gray-800 font-secondary">Cut through grease fast</strong> - professional-grade degreasers protect equipment and save time.
          </div>
        </div>
        <div class="sec-feature flex items-start g-0.5">
          <div class="sec-feature-icon">
            <i class="i-mdi:check w-24px h-24px text-green-500 flex gmt-0.25" />
          </div>
          <div class="sec-feature-content">
            <strong class="text-gray-800 font-secondary">Safe for food prep</strong> - every formulation tested for compliance in commercial kitchens.
          </div>
        </div>
        <div class="sec-feature flex items-start g-0.5">
          <div class="sec-feature-icon">
            <i class="i-mdi:check w-24px h-24px text-green-500 flex gmt-0.25" />
          </div>
          <div class="sec-feature-content">
            <strong class="text-gray-800 font-secondary">Reduce risks</strong> – fight bacteria and cross-contamination with targeted sanitisers.
          </div>
        </div>
        <div class="sec-feature flex items-start g-0.5">
          <div class="sec-feature-icon">
            <i class="i-mdi:check w-24px h-24px text-green-500 flex gmt-0.25" />
          </div>
          <div class="sec-feature-content">
            <strong class="text-gray-800 font-secondary">Eco-conscious cleaning</strong> – high performance without compromising sustainability.
          </div>
        </div>
        
       </div>

       <div class="sec-actions grid g-1.5">
        <p>We can assist you with the right products for your requirements.</p>

        <Button class="btn-primary gpy-1! gpx-2! rounded-full! text-white! place-self-start" @click="scrollToId('chkcontact', -80)">
          Free Assessment
        </Button>
        </div>
        
      </Frame>
    </Frame>

    <!-- Main Content -->
    <FrameTitleDirectory id="kitchen-products" class="gmb-4" dir="ltr">
      <template #title>
        <Frame class="sticky-container sticky top-32">
          <Frame class="sec-features-title bg-white gpy-1">
            <ATitle class="sticky-title" hairline="Professional Solutions" primary="Kitchen Products"
              subtitle="High-quality cleaning products for commercial kitchens" />
            <AContent>
              <div class="sec-products-select-filter">
                <ListboxDropdown
                  v-if="!isInitializing"
                  v-model="selectedCategory"
                  :options="dropdownOptions"
                  :label="selectedCategoryLabel"
                  placeholder="Select Category"
                  class="w-full"
                />
                <div v-else class="loading-placeholder">
                  Loading categories...
                </div>
              </div>
            </AContent>
          </Frame>
        </Frame>
      </template>
      
      <template #directory>
        <Wrap class="w-full grid grid-cols-[1fr] ts:gc-300px g-0 gmb-1">
          <div class="sec-results">
            <div class="sec-results-count"> 
              <span>{{ filteredProducts.length }}</span> products
              <span v-if="selectedCategory" style="margin-left: 0.5rem; color: #888;">
                in {{ selectedCategoryLabel }}
              </span>
              <span v-if="productsStore.isInitializing" class="text-xs text-gray-500 ml-2">
                (loading more...)
              </span>
            </div>
          </div>
        </Wrap>

        <Wrap v-if="loading || isInitializing" class="w-full flex justify-center gpy-4">
          <div class="loading-state">
            <Spinner />
            <p class="gmt-1 text-sm text-gray-600">Loading kitchen products...</p>
          </div>
        </Wrap>

        <Wrap v-else-if="filteredProducts.length === 0" class="w-full flex justify-center gpy-4">
          <div>No products found in this category.</div>
        </Wrap>

        <Wrap v-else class="w-full grid grid-cols-[1fr] ts:gc-300px g-0">
          <template v-for="product in filteredProducts" :key="product.id">
            <ProductCard :item="product" />
          </template>
        </Wrap>
      </template>
    </FrameTitleDirectory>


    <SectionContactWithRows id="contactus" />

  </Frame>
</template>

<style lang="scss" scoped>
.hero-section {
  border-radius: 1rem;
  margin-bottom: 2rem;
}

.sec-results-count {
  font-size: fs(-1);
  color: var(--base-600);
  text-transform: uppercase;
  span {
    color: var(--base-600);
  }
}

.sec-products-select-filter {
  margin-top: 1rem;
}

.loading-placeholder {
  padding: gr(0.5) gr(1);
  background-color: var(--base-100);
  border: 1px solid var(--base-300);
  border-radius: 4px;
  color: var(--base-500);
  font-size: fs(-1);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}
</style>
